// src/components/messaging/FloatingMessaging/FloatingConversationWindow.jsx - Avec suppression
import React, { useState, useRef, useEffect, useMemo } from 'react'
import {
  CFormInput,
  CSpinner,
  CDropdown,
  CDropdownToggle,
  CDropdownMenu,
  CDropdownItem,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import {
  cilX,
  cilWindowMinimize,
  cilPaperPlane,
  cilStar,
  cilArrowBottom,
  cilCheck,
  cilOptions,
  cilTrash,
  cilWarning,
  cilUserUnfollow,
  cilLockLocked,
  cilPeople,
  cilCheckAlt,
  cilBan,
  cilPaperclip,
} from '@coreui/icons'
import { get as apiGet, post as apiPost } from '../../utils/apiClient'
import { useSelector } from 'react-redux'
import { resolveSendEligibility, isReadOnlyError } from './sendEligibility'
import { useTranslation, Trans } from 'react-i18next'
import { useSocketContext } from '../../contexts/SocketContext'
import Avatar from '../../components/shared/Avatar'
import EmojiPicker from './EmojiPicker'
import MemberDetailModal from '../../components/MemberDetailModal'
import ConfirmationModal from './ConfirmationModal'
import MentorshipHeader from './MentorshipHeader'
import ConversationIcebreaker from './ConversationIcebreaker'
import AttachmentTray from './AttachmentTray'
import MessageAttachments from './MessageAttachments'
import PostLightbox from '../Posts/PostLightbox'
import { useEntitlements } from '../../contexts/EntitlementsContext'
import useAttachmentComposer from '../../hooks/useAttachmentComposer'
import { ACCEPT_ATTRIBUTE, canSubmitMessage, sendErrorKey } from '../../utils/messageAttachments'
import './FloatingConversationWindow.scss'

const FloatingConversationWindow = ({
  conversation,
  onClose,
  onMinimize,
  isMinimized,
  messagingState,
}) => {
  const {
    sendMessage,
    sendMessageWithAttachments,
    markConversationAsRead,
    deleteConversation,
    deleteMessage,
    loadMoreMessages,
    markConversationReadOnly,
  } = messagingState
  const currentUser = useSelector((state) => state.user?.user)
  const { t, i18n } = useTranslation()
  const { socket } = useSocketContext()
  const typingTimeoutRef = useRef(null)
  const isTypingRef = useRef(false)

  const [messageText, setMessageText] = useState('')
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [sending, setSending] = useState(false)
  const [showScrollButton, setShowScrollButton] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  // Sprint Vidal M2B B3b — template "premier message mentor".
  // Fetched une fois par conversation mentoring (cf. useEffect plus bas).
  // null = non fetch / inéligible, string = template pret a injecter.
  const [firstMessageTemplate, setFirstMessageTemplate] = useState(null)
  const [selectedMember, setSelectedMember] = useState(null)
  const [showMemberModal, setShowMemberModal] = useState(false)

  // États pour la suppression
  const [showDeleteConversationModal, setShowDeleteConversationModal] = useState(false)
  const [messageToDelete, setMessageToDelete] = useState(null)
  const [showDeleteMessageModal, setShowDeleteMessageModal] = useState(false)
  const [deletingConversation, setDeletingConversation] = useState(false)
  const [deletingMessage, setDeletingMessage] = useState(null)

  // Bridge intro states
  const [introActionLoading, setIntroActionLoading] = useState(null) // messageId being acted on
  const [introLocalStatus, setIntroLocalStatus] = useState({}) // messageId → 'accepted' | 'declined'

  // Sprint Vidal M2B B3b — fetch template "premier message mentor" si
  // conversation type=mentoring. Le backend retourne { canShow, template }
  // et applique 7 garde-fous (cf. mentoringController.getFirstMessageTemplate).
  // Si canShow=true, on stocke le template pour pouvoir l'injecter sur clic
  // du bouton "Utiliser un message de démarrage" (rendu juste au-dessus du
  // composer, conditionnel à textarea vide).
  //
  // IMPORTANT — reset SYNCHRONE au début pour éviter stale UI : sans ça,
  // un switch rapide conversation A (mentoring) → B (mentoring) laisserait
  // le template A visible pendant la latence fetch B → click rapide
  // injecterait le mauvais template.
  useEffect(() => {
    setFirstMessageTemplate(null)
    if (!conversation?.id || conversation.type !== 'mentoring') {
      return
    }
    let cancelled = false
    apiGet(`/mentoring/conversations/${conversation.id}/first-message-template`)
      .then((data) => {
        if (cancelled) return
        if (data?.canShow && data?.template) {
          setFirstMessageTemplate(data.template)
        }
        // Else : déjà null via reset synchrone ci-dessus, pas besoin de re-set
      })
      .catch(() => {
        // Fail silent : si 404/403/5xx, le template reste null (reset synchrone)
      })
    return () => {
      cancelled = true
    }
  }, [conversation?.id, conversation?.type])

  // A′.1 — attribution funnel radar→message : on retient la session radar
  // d'origine pour la joindre au PREMIER message envoyé dans cette conversation.
  const [radarAttribution, setRadarAttribution] = useState(null)

  // Radar prefill — pick up suggested message + origin from Network Radar (no auto-send)
  useEffect(() => {
    if (!window.__radarPrefill || !conversation) return
    const prefill = window.__radarPrefill
    // Match by checking if the conversation includes the target member
    const members = conversation.members || conversation.participants || []
    const hasTarget = members.some((m) => m.id === prefill.memberId)
    if (hasTarget) {
      if (prefill.message && !messageText) setMessageText(prefill.message)
      // Attribution captée même sans message pré-rempli (l'utilisateur peut
      // taper le sien) : le lien radar→message reste établi.
      if (prefill.radarSessionId) setRadarAttribution({ radarSessionId: prefill.radarSessionId })
      delete window.__radarPrefill // Only consume once applied
    }
  }, [conversation]) // eslint-disable-line react-hooks/exhaustive-deps

  // NOUVEAU: États pour l'infinite scroll (chargement intelligent des messages)
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false)
  const [allMessages, setAllMessages] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMoreMessages, setHasMoreMessages] = useState(false)

  const messagesEndRef = useRef(null)
  const messagesContainerRef = useRef(null)
  const inputRef = useRef(null)
  const emojiButtonRef = useRef(null)
  const windowRef = useRef(null)
  const isLoadingMoreRef = useRef(false) // Protection contre les appels multiples

  // Fonction pour obtenir le poste de l'utilisateur avec gestion de la confidentialité
  const getUserPosition = (member) => {
    if (!member) return t('messaging:conversation.unknown_user')

    if (member.privacy_settings?.position === false) {
      return t('messaging:window.position_hidden')
    }

    return member.position || t('messaging:window.position_empty')
  }

  // Cleanup du timeout au démontage
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current)
        handleTyping(false)
      }
    }
  }, [conversation?.id])

  // Gestion de l'indicateur "en train d'écrire"
  const handleTyping = (isTyping) => {
    if (!socket || !conversation?.id) return

    socket.emit('typing', {
      conversationId: conversation.id,
      isTyping,
    })
  }

  // Détecter quand l'utilisateur tape
  const handleInputChange = (e) => {
    const value = e.target.value
    setMessageText(value)

    // Toujours émettre "typing: true" pour rafraîchir l'état
    handleTyping(true)
    isTypingRef.current = true

    // Annuler le timeout précédent
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current)
    }

    // Après 3 secondes d'inactivité, émettre "typing: false"
    typingTimeoutRef.current = setTimeout(() => {
      handleTyping(false)
      isTypingRef.current = false
    }, 3000)
  }

  // Cleanup du timeout au démontage / changement de conversation
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current)
      }
      handleTyping(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation?.id])

  // Fonction pour ouvrir le modal de profil
  const handleAvatarClick = (member) => {
    if (member && member.id !== currentUser?.id) {
      setSelectedMember(member)
      setShowMemberModal(true)
    }
  }

  // Gestion de la suppression de conversation
  const handleDeleteConversation = async () => {
    if (deletingConversation) return

    try {
      setDeletingConversation(true)
      await deleteConversation(conversation.id)
      setShowDeleteConversationModal(false)
      onClose()
    } catch (error) {
      console.error('Erreur lors de la suppression de la conversation:', error)
    } finally {
      setDeletingConversation(false)
    }
  }

  // Gestion de la suppression de message
  const handleDeleteMessage = async (messageId, deleteForEveryone = false) => {
    if (deletingMessage === messageId) return

    try {
      setDeletingMessage(messageId)
      await deleteMessage(messageId, deleteForEveryone)
      setShowDeleteMessageModal(false)
      setMessageToDelete(null)
    } catch (error) {
      console.error('Erreur lors de la suppression du message:', error)
    } finally {
      setDeletingMessage(null)
    }
  }

  // Ouvrir le modal de suppression de message
  const handleOpenDeleteMessageModal = (message) => {
    setMessageToDelete(message)
    setShowDeleteMessageModal(true)
  }

  // Vérifier si les messages peuvent être envoyés
  const { canSendMessage, disabledReason } = useMemo(
    () => resolveSendEligibility({ conversation, currentUser }),
    [currentUser, conversation],
  )

  // ── Pièces jointes ────────────────────────────────────────────────────────
  // La capacité vient du BACKEND (`/me/entitlements`), qui la calcule avec la
  // même fonction que le gating d'envoi. On ne rejoue pas la règle de plan ici.
  // `=== true` et non un test de vérité : une capacité ABSENTE doit valoir
  // refus, jamais défaut permissif.
  const { capabilities } = useEntitlements()
  const canAttach = canSendMessage && capabilities?.sendMessageAttachments === true

  const composer = useAttachmentComposer({ enabled: canAttach })
  const fileInputRef = useRef(null)
  // null = pas d'envoi multipart en cours. 0 est une valeur légitime, d'où le
  // choix de null plutôt que 0 comme état de repos.
  const [uploadPercent, setUploadPercent] = useState(null)
  const [attachmentError, setAttachmentError] = useState(null)
  // Visionneuse plein écran. `null` = fermée. On garde les URL du MESSAGE
  // cliqué, et non de toute la conversation : naviguer d'une photo à l'autre
  // à l'intérieur d'un message est attendu, sauter dans un autre message ne
  // l'est pas.
  const [lightbox, setLightbox] = useState(null)

  /**
   * Traduit l'erreur serveur en message actionnable, sans jargon HTTP.
   * La correspondance statut -> clé vit dans `messageAttachments.js`, où elle
   * est testée ; ici on ne fait que la traduire.
   */
  const describeSendError = (error) => t(sendErrorKey(error?.status))

  // Message d'avertissement selon la raison
  const getDisabledWarningMessage = () => {
    switch (disabledReason) {
      case 'current_not_validated':
        return t('messaging:blocked.self_not_validated')
      case 'current_profile_incomplete':
        return t('messaging:blocked.self_incomplete_profile')
      case 'other_not_validated':
        return t('messaging:blocked.other_not_validated')
      case 'other_unavailable':
        return t('messaging:blocked.other_unavailable')
      case 'other_profile_incomplete':
        return t('messaging:blocked.other_incomplete_profile')
      default:
        return t('messaging:blocked.unavailable')
    }
  }

  // Informations d'affichage
  const { displayName, avatar, isOnline, otherMember } = useMemo(() => {
    if (!conversation) {
      return {
        displayName: t('messaging:window.conversation_fallback'),
        avatar: <div className="unknown-avatar">?</div>,
        isOnline: false,
        otherMember: null,
      }
    }

    if (conversation.type === 'group') {
      return {
        displayName: conversation.name || t('messaging:conversation.group_fallback'),
        avatar: <div className="group-avatar">{(conversation.name || 'G')[0].toUpperCase()}</div>,
        isOnline: false,
        otherMember: null,
      }
    }

    const otherMember = conversation.members?.find((m) => m.id !== currentUser?.id)
    const isMentoring = conversation.type === 'mentoring'

    const memberIsOnline =
      otherMember && messagingState?.onlineUsers
        ? messagingState.onlineUsers.has(otherMember.id)
        : false

    const baseName = otherMember
      ? `${otherMember.firstName} ${otherMember.lastName}`
      : t('messaging:conversation.unknown_user')
    const displayName = isMentoring ? `⭐ ${baseName}` : baseName

    return {
      displayName,
      avatar: otherMember ? (
        <div
          onClick={() => handleAvatarClick(otherMember)}
          style={{ cursor: 'pointer', position: 'relative' }}
          title={
            isMentoring
              ? t('messaging:window.view_profile_mentoring')
              : t('messaging:window.view_profile')
          }
        >
          <Avatar user={otherMember} size={40} />
          {isMentoring && (
            <span
              style={{
                position: 'absolute',
                bottom: -4,
                right: -4,
                background: '#fff',
                borderRadius: '50%',
                fontSize: '0.75rem',
                lineHeight: 1,
                padding: '1px 3px',
                border: '1px solid rgba(0,0,0,0.08)',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              }}
            >
              ⭐
            </span>
          )}
        </div>
      ) : (
        <div className="unknown-avatar">?</div>
      ),
      isOnline: memberIsOnline,
      otherMember,
    }
  }, [conversation, currentUser, messagingState?.onlineUsers])

  // NOUVEAU: Initialiser allMessages et pagination quand la conversation change
  useEffect(() => {
    if (conversation?.messages) {
      setAllMessages(conversation.messages)
      setCurrentPage(conversation.currentPage || 1)
      setHasMoreMessages(conversation.hasMoreMessages || false)
    }
  }, [
    conversation?.id,
    conversation?.messages,
    conversation?.currentPage,
    conversation?.hasMoreMessages,
  ])

  // Messages triés - utilise allMessages (qui contient les messages paginés)
  const sortedMessages = useMemo(() => {
    if (!allMessages || allMessages.length === 0) return []
    return [...allMessages].sort((a, b) => {
      const dateA = new Date(a.created_at || a.createdAt || 0)
      const dateB = new Date(b.created_at || b.createdAt || 0)
      return dateA - dateB
    })
  }, [allMessages])

  // Bridge intro handlers
  const handleIntroAccept = async (messageId) => {
    setIntroActionLoading(messageId)
    try {
      await apiPost(`/messaging/bridge-intro/${messageId}/accept`)
      setIntroLocalStatus((prev) => ({ ...prev, [messageId]: 'accepted' }))
    } catch (err) {
      console.error('Intro accept error:', err)
    } finally {
      setIntroActionLoading(null)
    }
  }

  const handleIntroDecline = async (messageId) => {
    setIntroActionLoading(messageId)
    try {
      await apiPost(`/messaging/bridge-intro/${messageId}/decline`)
      setIntroLocalStatus((prev) => ({ ...prev, [messageId]: 'declined' }))
    } catch (err) {
      console.error('Intro decline error:', err)
    } finally {
      setIntroActionLoading(null)
    }
  }

  // Récupération des typingUsers depuis le messagingState
  const typingUsers = messagingState?.typingUsers || []

  // Utilisateurs en train de taper dans cette conversation
  const typingUsersInConversation = useMemo(() => {
    if (!typingUsers || !conversation?.id) return []

    const filtered = typingUsers
      .filter((t) => t.conversationId === conversation.id && t.userId !== currentUser?.id)
      .map((t) => conversation.members?.find((m) => m.id === t.userId))
      .filter(Boolean)

    return filtered
  }, [typingUsers, conversation?.id, conversation?.members, currentUser?.id])

  // Auto-scroll
  const scrollToBottom = (smooth = true) => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: smooth ? 'smooth' : 'auto',
        block: 'end',
      })
    }
  }

  // NOUVEAU: Fonction pour charger plus de messages (infinite scroll)
  const handleLoadMoreMessages = async () => {
    // Protection: éviter les appels multiples simultanés
    if (isLoadingMoreRef.current || loadingMoreMessages || !hasMoreMessages || !conversation?.id) {
      return
    }

    isLoadingMoreRef.current = true
    setLoadingMoreMessages(true)

    try {
      console.log('📄 Chargement page', currentPage + 1, 'pour conversation', conversation.id)

      // Sauvegarder la position de scroll AVANT le chargement
      const container = messagesContainerRef.current
      const previousScrollHeight = container.scrollHeight
      const previousScrollTop = container.scrollTop

      // Appeler l'API pour charger la page suivante
      const result = await loadMoreMessages(conversation.id, currentPage)

      if (result && result.messages && result.messages.length > 0) {
        // Ajouter les nouveaux messages AU DÉBUT du tableau (messages plus anciens)
        setAllMessages((prevMessages) => [...result.messages, ...prevMessages])
        setCurrentPage(result.currentPage)
        setHasMoreMessages(result.hasMoreMessages)

        console.log('✅ Page', result.currentPage, 'chargée:', result.messages.length, 'messages')

        // Restaurer la position de scroll après le render (pour que l'utilisateur ne perde pas sa place)
        setTimeout(() => {
          if (container) {
            const newScrollHeight = container.scrollHeight
            const scrollDifference = newScrollHeight - previousScrollHeight
            // Ajuster le scroll pour maintenir la position visuelle
            container.scrollTop = previousScrollTop + scrollDifference
          }
        }, 50)
      } else {
        console.log('⚠️ Aucun message supplémentaire')
        setHasMoreMessages(false)
      }
    } catch (error) {
      console.error('❌ Erreur chargement messages supplémentaires:', error)
      setHasMoreMessages(false)
    } finally {
      isLoadingMoreRef.current = false
      setLoadingMoreMessages(false)
    }
  }

  // Gestion du scroll pour le bouton "aller en bas" ET pour l'infinite scroll
  const handleScroll = () => {
    if (messagesContainerRef.current) {
      const container = messagesContainerRef.current

      // Gestion bouton "aller en bas" (existant)
      const isNearBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight < 100
      setShowScrollButton(!isNearBottom)

      // NOUVEAU: Détection scroll vers le haut pour charger plus de messages
      const isNearTop = container.scrollTop < 150
      if (isNearTop && hasMoreMessages && !loadingMoreMessages && !isLoadingMoreRef.current) {
        handleLoadMoreMessages()
      }
    }
  }

  /**
   * LA RÈGLE, identique au backend :
   *   texte non vide OU au moins un fichier prêt -> envoi
   *   texte vide ET aucun fichier                -> rien
   *
   * C'était le verrou frontend : la condition d'origine portait sur
   * `messageText.trim()` SEUL, donc un message ne portant que des fichiers
   * était refusé côté client alors que le backend l'accepte.
   */
  const canSubmit = canSubmitMessage({
    text: messageText,
    readyCount: composer.readyFiles.length,
    blocked: composer.blocked,
    preparing: composer.preparing,
  })

  // Envoyer message avec maintien du focus
  const handleSendMessage = async (e) => {
    e.preventDefault()
    if (!canSubmit || sending || !conversation?.id) return

    setSending(true)
    setAttachmentError(null)
    const messageContent = messageText.trim()
    // Capturé AVANT de vider le composeur : `composer.clear()` révoque les
    // aperçus, mais les File eux-mêmes restent valides une fois référencés.
    const outgoingFiles = composer.readyFiles

    setMessageText('')

    // Arrêter l'indicateur "typing" immédiatement
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current)
    }
    handleTyping(false)
    isTypingRef.current = false

    try {
      if (outgoingFiles.length > 0) {
        setUploadPercent(0)
        await sendMessageWithAttachments({
          conversationId: conversation.id,
          content: messageContent,
          files: outgoingFiles,
          // Garde contre NaN : `uploadFile` calcule `loaded * 100 / total`, et
          // `total` est absent quand la longueur du corps n'est pas connue.
          // Sans ce filtre, la barre afficherait « Envoi… NaN % ».
          onProgress: (percent) => setUploadPercent(Number.isFinite(percent) ? percent : 0),
        })
        composer.clear()
      } else {
        // A′.1 : n'attribuer au radar que le PREMIER message de la conversation.
        await sendMessage(conversation.id, messageContent, radarAttribution || undefined)
      }
      if (radarAttribution) setRadarAttribution(null)

      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus()
        }
        scrollToBottom(true)
      }, 50)
    } catch (error) {
      console.error('Erreur envoi message:', error)
      // Lecture seule découverte à l'envoi (interlocuteur effacé pendant que la fenêtre était ouverte) : le serveur l'a
      // CONFIRMÉE. On la retient dans le singleton (conservée à la fermeture / réouverture, jamais annulée par une liste
      // périmée) ; la zone de saisie est alors remplacée par le message explicite. Le texte saisi est restauré ci-dessous.
      if (isReadOnlyError(error)) markConversationReadOnly?.(conversation.id)
      // Le texte est restauré pour ne rien perdre de la saisie. Les fichiers
      // restent dans le composeur, donc un simple nouvel envoi les reprend :
      // c'est pour ça que `composer.clear()` n'est appelé qu'en cas de succès.
      setMessageText(messageContent)
      if (outgoingFiles.length > 0) setAttachmentError(describeSendError(error))
    } finally {
      setSending(false)
      setUploadPercent(null)
    }
  }

  // Gestion touches
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage(e)
    }
  }

  // Emoji
  const handleEmojiSelect = (emoji) => {
    setMessageText((prev) => prev + emoji.native)
    setShowEmojiPicker(false)
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus()
      }
    }, 10)
  }

  // Format heure avec gestion robuste des dates
  const formatMessageTime = (timestamp) => {
    if (!timestamp) return 'maintenant'

    let date
    if (timestamp instanceof Date) {
      date = timestamp
    } else if (typeof timestamp === 'string') {
      date = new Date(timestamp)
    } else if (typeof timestamp === 'number') {
      date = new Date(timestamp)
    } else {
      return 'maintenant'
    }

    if (isNaN(date.getTime())) {
      console.warn('Date invalide reçue:', timestamp)
      return 'maintenant'
    }

    const now = new Date()
    const diffMs = now - date
    const diffMins = Math.floor(diffMs / 60000)

    if (diffMins < 1) return t('messaging:time.now')
    if (diffMins < 60) return t('messaging:time.minutes_short', { count: diffMins })
    if (diffMins < 1440)
      return t('messaging:time.hours_short', { count: Math.floor(diffMins / 60) })

    // Locale de l'UI et non 'fr-FR' figé.
    return date.toLocaleDateString(i18n.language, {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // Marquer comme lu
  useEffect(() => {
    if (conversation?.unreadCount > 0 && !isMinimized) {
      markConversationAsRead(conversation.id)
    }
  }, [conversation?.id, conversation?.unreadCount, isMinimized, markConversationAsRead])

  // Auto-scroll
  useEffect(() => {
    if (!isMinimized) {
      setTimeout(() => scrollToBottom(false), 100)
    }
  }, [sortedMessages.length, isMinimized])

  // Gestion du focus de la fenêtre
  useEffect(() => {
    const handleFocusIn = (e) => {
      if (windowRef.current && windowRef.current.contains(e.target)) {
        setIsFocused(true)
      }
    }

    const handleFocusOut = (e) => {
      if (windowRef.current && !windowRef.current.contains(e.relatedTarget)) {
        setIsFocused(false)
      }
    }

    const handleClick = (e) => {
      if (windowRef.current && windowRef.current.contains(e.target)) {
        setIsFocused(true)
      } else {
        setIsFocused(false)
      }
    }

    document.addEventListener('focusin', handleFocusIn)
    document.addEventListener('focusout', handleFocusOut)
    document.addEventListener('click', handleClick)

    return () => {
      document.removeEventListener('focusin', handleFocusIn)
      document.removeEventListener('focusout', handleFocusOut)
      document.removeEventListener('click', handleClick)
    }
  }, [])

  // Focus input avec maintien après les updates
  useEffect(() => {
    if (inputRef.current && !isMinimized && !sending) {
      const timer = setTimeout(() => {
        if (inputRef.current && document.activeElement !== inputRef.current) {
          inputRef.current.focus()
        }
      }, 100)

      return () => clearTimeout(timer)
    }
  }, [isMinimized, sending, sortedMessages.length])

  if (!conversation) return null

  // Vue minimisée
  if (isMinimized) {
    const minimizedOtherMember =
      conversation.type === 'group'
        ? null
        : conversation.members?.find((m) => m.id !== currentUser?.id)
    const isMentoring = conversation.type === 'mentoring'
    // Le prénom seul pour le label compact ; on garde l'étoile mentoring en préfixe.
    const minimizedLabel =
      conversation.type === 'group'
        ? (conversation.name || t('messaging:conversation.group_fallback')).split(' ')[0]
        : minimizedOtherMember
          ? `${isMentoring ? '⭐ ' : ''}${minimizedOtherMember.firstName || ''}`
          : displayName.split(' ')[0]

    return (
      <div className="floating-conversation-window minimized">
        <div className="floating-conversation-minimized">
          <div className="minimized-content" onClick={onMinimize}>
            <div className="minimized-avatar">
              {conversation.type === 'group' ? (
                <div className="group-avatar-mini">
                  {(conversation.name || 'G')[0].toUpperCase()}
                </div>
              ) : minimizedOtherMember ? (
                <div className="user-avatar-container" style={{ position: 'relative' }}>
                  <Avatar user={minimizedOtherMember} size={40} />
                  {isOnline && <span className="online-dot"></span>}
                  {isMentoring && (
                    <span
                      title={t('messaging:conversation.mentoring_badge')}
                      style={{
                        position: 'absolute',
                        bottom: -2,
                        right: -2,
                        background: '#fff',
                        borderRadius: '50%',
                        fontSize: '0.7rem',
                        lineHeight: 1,
                        padding: '1px 2px',
                        border: '1px solid rgba(0,0,0,0.08)',
                      }}
                    >
                      ⭐
                    </span>
                  )}
                </div>
              ) : (
                <div className="unknown-avatar-mini">?</div>
              )}
            </div>

            <div className="minimized-info">
              <div className="minimized-name">{minimizedLabel}</div>
              {conversation.unreadCount > 0 && (
                <div className="minimized-unread-badge">
                  {conversation.unreadCount > 9 ? '9+' : conversation.unreadCount}
                </div>
              )}
            </div>

            <button
              className="minimized-close-btn"
              onClick={(e) => {
                e.stopPropagation()
                onClose()
              }}
            >
              <CIcon icon={cilX} size="sm" />
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Vue complète
  return (
    <div
      className={`floating-conversation-window ${isFocused ? 'focused' : ''}`}
      ref={windowRef}
      tabIndex={-1}
      // Les gestionnaires sont posés sur la FENÊTRE et non sur la seule zone de
      // saisie : on dépose naturellement une photo « dans la conversation »,
      // pas sur un champ de quelques pixels de haut. Ils sont inertes quand la
      // capacité est absente.
      {...composer.dragHandlers}
    >
      {composer.dragging && (
        <div className="conversation-drop-overlay" aria-hidden="true">
          <CIcon icon={cilPaperclip} size="xl" />
          <strong>{t('messaging:attachments.drop_here')}</strong>
          <span>{t('messaging:attachments.drop_hint')}</span>
        </div>
      )}
      <div className="floating-conversation-expanded">
        {/* En-tête moderne avec menu de suppression */}
        <div className={`conversation-header-premium ${isFocused ? 'header-focused' : ''}`}>
          <div className="header-user-info">
            <div className="header-avatar-container">
              {avatar}
              {isOnline && <span className="status-indicator online"></span>}
            </div>
            <div className="header-text-info">
              <h3 className="user-display-name">{displayName}</h3>
              <p className="user-status-text">
                {conversation.type === 'group'
                  ? t('messaging:window.members_count', {
                      count: conversation.members?.length || 0,
                    })
                  : conversation.type === 'mentoring'
                    ? t('messaging:window.mentoring_conversation')
                    : otherMember
                      ? getUserPosition(otherMember)
                      : t('messaging:conversation.unknown_user')}
              </p>
            </div>
          </div>

          <div className="header-action-buttons">
            {/* Menu options avec suppression */}
            <CDropdown className="header-options-dropdown">
              <CDropdownToggle
                className="header-action-btn options-btn"
                caret={false}
                title={t('messaging:window.options')}
              >
                <CIcon icon={cilOptions} />
              </CDropdownToggle>
              <CDropdownMenu className="options-menu">
                <CDropdownItem
                  onClick={() => setShowDeleteConversationModal(true)}
                  className="delete-conversation-item"
                >
                  <CIcon icon={cilTrash} className="me-2" />
                  Supprimer la conversation
                </CDropdownItem>
              </CDropdownMenu>
            </CDropdown>

            <button
              className="header-action-btn minimize-btn"
              onClick={onMinimize}
              title={t('messaging:window.minimize')}
            >
              <CIcon icon={cilWindowMinimize} />
            </button>
            <button
              className="header-action-btn close-btn"
              onClick={onClose}
              title={t('common:close')}
            >
              <CIcon icon={cilX} />
            </button>
          </div>
        </div>

        {/* Sprint Vidal M2B B4b — Header mentorat léger (contexte + bouton clôture).
            Affiché UNIQUEMENT si conversation type='mentoring'. Le composant
            fetch lui-même son data + gère le toast/confirmation modal. Si fail
            silent (404 non-participant, 5xx) il render null sans casser le chat. */}
        {conversation?.type === 'mentoring' && (
          <MentorshipHeader conversationId={conversation.id} />
        )}

        {/* Zone de messages */}
        <div
          className="messages-container-premium"
          ref={messagesContainerRef}
          onScroll={handleScroll}
        >
          {/* Icebreaker : intérêts en commun (1:1 uniquement, masqué si aucun). */}
          {otherMember && <ConversationIcebreaker otherMemberId={otherMember.id} />}
          {sortedMessages.length === 0 ? (
            <div className="empty-conversation-state">
              <div className="empty-avatar-large">{avatar}</div>
              <h4 className="empty-title">{displayName}</h4>
              <p className="empty-subtitle">
                {t('messaging:window.empty_subtitle', { name: displayName })}
              </p>
              <p className="empty-hint">{t('messaging:window.empty_hint')}</p>
            </div>
          ) : (
            <div className="messages-timeline">
              {/* NOUVEAU: Spinner de chargement pour l'infinite scroll */}
              {loadingMoreMessages && (
                <div
                  className="loading-more-messages"
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '10px 0',
                    gap: '8px',
                    color: 'var(--cui-secondary)',
                    fontSize: '0.875rem',
                  }}
                >
                  <CSpinner size="sm" />
                  <span>{t('messaging:window.loading_messages')}</span>
                </div>
              )}

              {sortedMessages.map((message, index) => {
                const isOwn = message.sender_id === currentUser?.id
                const prevMessage = sortedMessages[index - 1]
                const nextMessage = sortedMessages[index + 1]

                const showAvatar =
                  !isOwn && (!nextMessage || nextMessage.sender_id !== message.sender_id)
                const isGrouped =
                  prevMessage &&
                  prevMessage.sender_id === message.sender_id &&
                  new Date(message.created_at || message.createdAt) -
                    new Date(prevMessage.created_at || prevMessage.createdAt) <
                    300000

                // Détermine si c'est le dernier message de l'utilisateur
                // en vérifiant qu'il n'y a pas de message suivant du même sender
                const isLastOwnMessage =
                  isOwn &&
                  !message.temporary &&
                  !String(message.id).startsWith('temp-') &&
                  (!nextMessage || nextMessage.sender_id !== currentUser?.id)

                const isBeingDeleted = deletingMessage === message.id

                return (
                  <div
                    key={message.id || index}
                    className={`message-container ${isOwn ? 'message-own' : 'message-other'} ${isGrouped ? 'message-grouped' : ''} ${isBeingDeleted ? 'message-deleting' : ''}`}
                  >
                    {!isOwn && (
                      <div className="message-avatar-space">
                        {showAvatar && (
                          <div
                            className="message-sender-avatar"
                            onClick={() => handleAvatarClick(message.sender)}
                            style={{ cursor: message.sender ? 'pointer' : 'default' }}
                            title={message.sender ? t('messaging:window.view_profile') : ''}
                          >
                            {message.sender ? (
                              <Avatar user={message.sender} size={32} />
                            ) : (
                              <div className="unknown-sender-avatar">?</div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="message-content-wrapper">
                      <div className="message-bubble-container">
                        <div
                          className={`message-bubble-premium ${isOwn ? 'bubble-sent' : 'bubble-received'}`}
                          onContextMenu={(e) => {
                            e.preventDefault()
                            if (isOwn) {
                              handleOpenDeleteMessageModal(message)
                            }
                          }}
                        >
                          {/* NOUVEAU: Bouton de suppression pour les messages propres */}
                          {isOwn && !isBeingDeleted && (
                            <button
                              className="message-delete-btn"
                              onClick={() => handleOpenDeleteMessageModal(message)}
                              title={t('messaging:delete_message_modal.title')}
                            >
                              <CIcon icon={cilX} />
                            </button>
                          )}

                          {/* Loading pendant suppression */}
                          {isBeingDeleted && (
                            <div className="message-deleting-overlay">
                              <CSpinner size="sm" />
                            </div>
                          )}

                          {/* Bridge intro card */}
                          {message.metadata?.type === 'bridge_intro_request' &&
                            (() => {
                              const meta = message.metadata
                              const status = introLocalStatus[message.id] || meta.status
                              const isBridge = currentUser?.id === meta.bridgeId
                              const isLoading = introActionLoading === message.id
                              return (
                                <div className={`bridge-intro-card bridge-intro-${status}`}>
                                  <div className="bridge-intro-card-header">
                                    <CIcon icon={cilPeople} size="sm" />
                                    <span className="bridge-intro-card-title">
                                      {t('messaging:bridge_intro.card_title')}
                                    </span>
                                  </div>
                                  <p className="bridge-intro-card-text">
                                    {meta.targetFirstName} {meta.targetLastName}
                                    {meta.targetPosition ? ` — ${meta.targetPosition}` : ''}
                                    {meta.targetCompany
                                      ? t('messaging:bridge_intro.at_company', {
                                          company: meta.targetCompany,
                                        })
                                      : ''}
                                  </p>
                                  {status === 'pending' && isBridge && (
                                    <div className="bridge-intro-card-actions">
                                      <button
                                        className="bridge-intro-btn bridge-intro-btn-accept"
                                        onClick={() => handleIntroAccept(message.id)}
                                        disabled={isLoading}
                                      >
                                        {isLoading ? (
                                          <CSpinner size="sm" />
                                        ) : (
                                          <CIcon icon={cilCheckAlt} size="sm" />
                                        )}
                                        {t('messaging:bridge_intro.accept')}
                                      </button>
                                      <button
                                        className="bridge-intro-btn bridge-intro-btn-decline"
                                        onClick={() => handleIntroDecline(message.id)}
                                        disabled={isLoading}
                                      >
                                        <CIcon icon={cilBan} size="sm" />
                                        {t('messaging:bridge_intro.decline')}
                                      </button>
                                    </div>
                                  )}
                                  {status === 'accepted' && (
                                    <div className="bridge-intro-card-status bridge-intro-status-accepted">
                                      <CIcon icon={cilCheckAlt} size="sm" />
                                      {t('messaging:bridge_intro.accepted')}
                                    </div>
                                  )}
                                  {status === 'declined' && (
                                    <div className="bridge-intro-card-status bridge-intro-status-declined">
                                      <CIcon icon={cilBan} size="sm" />
                                      {t('messaging:bridge_intro.declined')}
                                    </div>
                                  )}
                                  {status === 'pending' && !isBridge && (
                                    <div className="bridge-intro-card-status bridge-intro-status-pending">
                                      {t('messaging:bridge_intro.pending')}
                                    </div>
                                  )}
                                </div>
                              )
                            })()}

                          {/* Un message ne portant que des fichiers a un
                              contenu vide : rendre la div produirait une ligne
                              blanche au-dessus des vignettes. */}
                          {message.content ? (
                            <div className="bubble-content">{message.content}</div>
                          ) : null}
                          {message.attachments?.length > 0 && (
                            <MessageAttachments
                              attachments={message.attachments}
                              onOpenImage={(images, index) => setLightbox({ images, index })}
                            />
                          )}
                          <div className="bubble-metadata">
                            <span className="message-timestamp">
                              {formatMessageTime(message.created_at || message.createdAt)}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Statut de lecture en dessous du message */}
                      {isLastOwnMessage && (
                        <div className="message-read-status">
                          {message.notifications && message.notifications.length > 0 ? (
                            <>
                              <span className="read-status-text">Vu</span>
                              {message.notifications[0]?.recipient && (
                                <Avatar
                                  user={message.notifications[0].recipient}
                                  size={16}
                                  className="read-receipt-avatar-wrapper"
                                />
                              )}
                            </>
                          ) : (
                            <CIcon icon={cilCheck} className="status-delivered-icon" />
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {showScrollButton && (
            <button
              className="scroll-to-bottom-premium"
              onClick={() => scrollToBottom(true)}
              title={t('messaging:window.scroll_to_recent')}
            >
              <CIcon icon={cilArrowBottom} />
            </button>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Indicateur "en train d'écrire" */}
        {typingUsersInConversation.length > 0 && (
          <div className="typing-indicator-container">
            <div className="typing-indicator">
              <span className="typing-dot"></span>
              <span className="typing-dot"></span>
              <span className="typing-dot"></span>
            </div>
            <span className="typing-text">
              {typingUsersInConversation.length === 1
                ? t('messaging:window.typing_single', {
                    name: typingUsersInConversation[0].firstName,
                  })
                : t('messaging:window.typing_multiple', {
                    count: typingUsersInConversation.length,
                  })}
            </span>
          </div>
        )}

        {/* Zone de composition */}
        <div className="message-composer-premium">
          {!canSendMessage && (
            <div className="messaging-disabled-warning">
              <CIcon icon={cilLockLocked} className="warning-icon" />
              <span>{getDisabledWarningMessage()}</span>
            </div>
          )}

          {/* Sprint Vidal M2B B3b — bouton "Utiliser un message de démarrage".
              Visible UNIQUEMENT si :
                - le template a été fourni par le backend (canShow=true)
                - le textarea est strictement vide (anti-écrasement saisie user)
                - la messagerie est active (canSendMessage)
                - pas d'envoi en cours (anti race click pendant submit)
              Click → injecte le template dans le textarea + focus.
              L'user peut éditer librement avant d'envoyer.
              Wording sober (pas d'emoji, pas d'IA, pas de gimmick) — cohérent
              avec le ton B2B école. */}
          {canSendMessage &&
            !sending &&
            firstMessageTemplate &&
            messageText.trim().length === 0 && (
              <div
                className="composer-template-hint"
                style={{
                  padding: '6px 10px',
                  marginBottom: '6px',
                  fontSize: '0.85rem',
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setMessageText(firstMessageTemplate)
                    setTimeout(() => inputRef.current?.focus(), 10)
                  }}
                  style={{
                    background: 'transparent',
                    border: '1px solid var(--cui-border-color, rgba(0,0,0,0.15))',
                    borderRadius: '6px',
                    padding: '4px 10px',
                    cursor: 'pointer',
                    color: 'var(--cui-body-color, #444)',
                    fontSize: '0.85rem',
                  }}
                >
                  {t('messaging:window.use_starter')}
                </button>
              </div>
            )}

          <AttachmentTray
            entries={composer.entries}
            onRemove={composer.remove}
            limits={composer.limits}
            preparing={composer.preparing}
            uploadPercent={uploadPercent}
            disabled={sending}
          />

          {attachmentError && (
            <div className="attachment-tray__error" role="alert">
              <CIcon icon={cilWarning} />
              <span>{attachmentError}</span>
            </div>
          )}

          <form onSubmit={handleSendMessage} className="composer-form-premium">
            <div className={`composer-input-container ${!canSendMessage ? 'disabled' : ''}`}>
              {/* Trombone MASQUÉ — et non désactivé — quand le plan ne le permet
                  pas : un bouton grisé sur une fonctionnalité non souscrite est
                  une promesse qu'on ne tient pas. La capacité étant absente par
                  défaut, rien ne s'affiche tant que le backend ne l'a pas
                  confirmée. */}
              {canAttach && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={ACCEPT_ATTRIBUTE}
                    className="composer-file-input"
                    onChange={(event) => {
                      composer.addFiles(event.target.files)
                      // Réinitialisé pour que resélectionner LE MÊME fichier
                      // déclenche à nouveau `change` : sinon, retirer une photo
                      // puis la rechoisir ne produit rien.
                      event.target.value = ''
                    }}
                    tabIndex={-1}
                    aria-hidden="true"
                  />
                  <button
                    type="button"
                    className="composer-attach-button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={sending || composer.preparing}
                    title={t('messaging:attachments.add')}
                    aria-label={t('messaging:attachments.add')}
                    onMouseDown={(e) => {
                      e.preventDefault()
                    }}
                  >
                    <CIcon icon={cilPaperclip} />
                  </button>
                </>
              )}

              <button
                type="button"
                className="composer-emoji-button"
                onClick={() => canSendMessage && setShowEmojiPicker(!showEmojiPicker)}
                ref={emojiButtonRef}
                title={
                  canSendMessage
                    ? t('messaging:window.add_emoji')
                    : t('messaging:window.messaging_disabled')
                }
                disabled={!canSendMessage}
                onMouseDown={(e) => {
                  e.preventDefault()
                }}
                style={!canSendMessage ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
              >
                <CIcon icon={cilStar} />
              </button>

              <div className="composer-text-container">
                <CFormInput
                  ref={inputRef}
                  value={messageText}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  // Ne consomme l'événement QUE s'il portait des fichiers : un
                  // Ctrl+V de texte doit continuer de coller du texte.
                  onPaste={composer.onPaste}
                  onFocus={() => {
                    // Marquer la conversation comme lue quand l'utilisateur commence à écrire
                    if (conversation?.id) {
                      markConversationAsRead(conversation.id)
                    }
                  }}
                  placeholder={
                    canSendMessage
                      ? t('messaging:window.write_placeholder')
                      : t('messaging:window.messaging_disabled')
                  }
                  disabled={sending || !canSendMessage}
                  className="composer-text-input"
                  autoComplete="off"
                  style={!canSendMessage ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                  onBlur={(e) => {
                    if (e.relatedTarget?.type === 'submit') {
                      e.preventDefault()
                      setTimeout(() => {
                        if (inputRef.current) {
                          inputRef.current.focus()
                        }
                      }, 10)
                    }
                  }}
                />
              </div>

              <button
                type="submit"
                disabled={!canSubmit || sending || !canSendMessage}
                className={`composer-send-button ${canSubmit && canSendMessage ? 'send-ready' : 'send-disabled'}`}
                title={
                  canSendMessage
                    ? t('messaging:window.send_message')
                    : t('messaging:window.messaging_disabled')
                }
                onMouseDown={(e) => {
                  e.preventDefault()
                }}
                onClick={handleSendMessage}
                style={!canSendMessage ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
              >
                {sending ? <CSpinner size="sm" /> : <CIcon icon={cilPaperPlane} />}
              </button>
            </div>
          </form>

          {showEmojiPicker && canSendMessage && (
            <div className="emoji-picker-container">
              <EmojiPicker
                onSelect={handleEmojiSelect}
                onClose={() => setShowEmojiPicker(false)}
                buttonRef={emojiButtonRef}
              />
            </div>
          )}
        </div>
      </div>

      {/* Visionneuse plein écran — RÉUTILISE celle du feed plutôt qu'une
          seconde implémentation. `postId` et les identifications ne sont pas
          passés : `isPostAuthor`/`isAdmin` valant false par défaut, aucun
          contrôle d'identification n'apparaît. */}
      {lightbox && (
        <PostLightbox
          images={lightbox.images}
          initialIndex={lightbox.index}
          onClose={() => setLightbox(null)}
        />
      )}

      {/* Modal de profil membre */}
      <MemberDetailModal
        visible={showMemberModal}
        onClose={() => setShowMemberModal(false)}
        member={selectedMember}
        isAdmin={currentUser?.role === 'admin'}
        isLoading={false}
      />

      {/* Modal de confirmation suppression conversation */}
      <ConfirmationModal
        visible={showDeleteConversationModal}
        onClose={() => setShowDeleteConversationModal(false)}
        onConfirm={handleDeleteConversation}
        title={t('messaging:delete_conversation.title')}
        message={
          <div>
            <p>
              <Trans
                i18nKey="messaging:delete_conversation.confirm"
                values={{ name: displayName }}
                components={{ strong: <strong /> }}
              />
            </p>
            <div className="delete-conversation-info">
              <div className="info-item">
                <CIcon icon={cilUserUnfollow} className="info-icon" />
                <span>{t('messaging:delete_conversation.info_local_only')}</span>
              </div>
              <div className="info-item">
                <CIcon icon={cilWarning} className="info-icon warning" />
                <span>
                  {t('messaging:delete_conversation.info_reappears', { name: displayName })}
                </span>
              </div>
            </div>
          </div>
        }
        confirmText={t('common:delete')}
        cancelText={t('common:cancel')}
        confirmButtonClass="btn-danger"
        loading={deletingConversation}
        icon={cilTrash}
      />

      {/* Modal de confirmation suppression message */}
      <ConfirmationModal
        visible={showDeleteMessageModal}
        onClose={() => {
          setShowDeleteMessageModal(false)
          setMessageToDelete(null)
        }}
        title={t('messaging:delete_message_modal.title')}
        message={
          <div>
            <p>{t('messaging:delete_message_modal.question')}</p>
            <div className="message-preview">
              <em>"{messageToDelete?.content}"</em>
            </div>
          </div>
        }
        customActions={
          <div className="delete-message-actions">
            <button
              type="button"
              className="btn btn-outline-secondary me-2"
              onClick={() => {
                setShowDeleteMessageModal(false)
                setMessageToDelete(null)
              }}
              disabled={deletingMessage}
            >
              {t('common:cancel')}
            </button>
            <button
              type="button"
              className="btn btn-warning me-2"
              onClick={() => handleDeleteMessage(messageToDelete?.id, false)}
              disabled={deletingMessage}
            >
              {deletingMessage ? <CSpinner size="sm" className="me-1" /> : null}
              {t('messaging:delete_message_modal.for_me')}
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => handleDeleteMessage(messageToDelete?.id, true)}
              disabled={deletingMessage}
            >
              {deletingMessage ? <CSpinner size="sm" className="me-1" /> : null}
              {t('messaging:delete_message_modal.for_everyone')}
            </button>
          </div>
        }
        hideDefaultActions={true}
        icon={cilTrash}
      />
    </div>
  )
}

export default FloatingConversationWindow
